// =============================================================================
// SendAgreementPanel.tsx
//
// Two export shapes for the "Send install agreement" action:
//
//   <SendAgreementHero deal={...} />   — Big card at top of DealDetail.
//                                         Status-aware: not-yet-approved /
//                                         approved-awaiting-send / sent-awaiting-
//                                         signature / signed.
//
//   <SendAgreementButton deal={...} /> — Compact button for deal cards
//                                         (ChadApprovalsTab, MyPipeline,
//                                         dispatch board). Opens the same
//                                         modal as the hero.
//
// Both share the internal <SendAgreementModal /> which holds the email/sms/both
// chips + template preview + delivery history + edit-message link.
//
// Mount conditions: only shows when agreement_status === 'approved' AND not
// yet signed. Pre-approval / already-signed states render an informational
// pill but no action.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { fetchEdge, readEdgeJson } from "../../lib/edgeError";
import { SUPABASE_URL } from "../../lib/supabase";

// --- types ----------------------------------------------------------------

export type AgreementDeal = {
  id: string;
  homeowner_name: string | null;
  homeowner_email: string | null;
  homeowner_phone: string | null;
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  agreement_status: string | null;
  install_agreement_signed_date: string | null;
  install_agreement_pdf_url: string | null;
  ho_preferred_build_date: string | null;
  agreement_data: Record<string, any> | null;
  owner_id?: string | null;
  split_rm_id?: string | null;
};

type Props = {
  supabase: SupabaseClient;
  deal: AgreementDeal;
  currentUserId: string;
  currentUserFullName?: string;
  onUpdated?: () => void;
};

type Delivery = {
  id: string;
  sent_at: string;
  sent_by: string | null;
  method: "email" | "sms" | "both";
  recipient_email: string | null;
  recipient_phone: string | null;
  email_status: string;
  sms_status: string;
  first_opened_at: string | null;
  open_count: number;
  signed_at: string | null;
  signed_by_name: string | null;
  signing_link_url: string | null;
};

type TemplateSet = {
  emailSubject: string;
  emailBody: string;
  smsBody: string;
  isPersonalOverride: boolean;
};

// --- helpers --------------------------------------------------------------

const money = (v: number | string | null | undefined) =>
  Number(v ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const fmtDate = (d: string | null | undefined) => {
  if (!d) return "TBD";
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d + "T12:00:00" : d;
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const relativeTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const firstName = (full: string | null | undefined) =>
  full?.trim().split(/\s+/)[0] ?? "there";

const mergeTemplate = (tpl: string, vars: Record<string, string>) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);

const canSend = (deal: AgreementDeal) =>
  deal.agreement_status === "approved" && !deal.install_agreement_signed_date;

// --- shared modal ---------------------------------------------------------

function SendAgreementModal({
  supabase, deal, currentUserId, currentUserFullName, onClose, onSent,
}: Props & { onClose: () => void; onSent: () => void }) {
  const [previewTab, setPreviewTab] = useState<"email" | "sms">("email");
  const [sending, setSending] = useState<null | "email" | "sms" | "both">(null);
  const [err, setErr] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateSet | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [ownerRm, setOwnerRm] = useState<{ email: string | null; full_name: string | null } | null>(null);
  const [splitRm, setSplitRm] = useState<{ email: string | null; full_name: string | null } | null>(null);

  // Look up deal owner + split RM emails so the preview shows the real From/CC
  // that the edge function will use on send.
  useEffect(() => {
    const ids: string[] = [];
    if (deal.owner_id) ids.push(deal.owner_id);
    if (deal.split_rm_id && deal.split_rm_id !== deal.owner_id) ids.push(deal.split_rm_id);
    if (ids.length === 0) { setOwnerRm(null); setSplitRm(null); return; }
    let cancel = false;
    supabase.from("team_roster").select("id, email, full_name").in("id", ids).then(({ data }) => {
      if (cancel) return;
      setOwnerRm(data?.find((r: any) => r.id === deal.owner_id) ?? null);
      setSplitRm(deal.split_rm_id ? data?.find((r: any) => r.id === deal.split_rm_id) ?? null : null);
    });
    return () => { cancel = true; };
  }, [supabase, deal.owner_id, deal.split_rm_id]);

  // template load (extracted so we can refetch after a save in the edit modal)
  const loadTemplates = useCallback(async () => {
    const [overrideRes, sysSubjRes, sysEmailRes, sysSmsRes] = await Promise.all([
      supabase.from("agreement_template_overrides").select("*").eq("user_id", currentUserId).maybeSingle(),
      supabase.from("finance_settings").select("value_jsonb").eq("key", "agreement_template_email_subject").single(),
      supabase.from("finance_settings").select("value_jsonb").eq("key", "agreement_template_email_body").single(),
      supabase.from("finance_settings").select("value_jsonb").eq("key", "agreement_template_sms_body").single(),
    ]);
    const ov = overrideRes.data;
    setTemplates({
      emailSubject: ov?.email_subject ?? String(sysSubjRes.data?.value_jsonb ?? ""),
      emailBody:    ov?.email_body    ?? String(sysEmailRes.data?.value_jsonb ?? ""),
      smsBody:      ov?.sms_body      ?? String(sysSmsRes.data?.value_jsonb ?? ""),
      isPersonalOverride: !!ov,
    });
  }, [supabase, currentUserId]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const previewVars = useMemo(() => {
    const ad = deal.agreement_data ?? {};
    // ho_out_of_pocket on newer rows, homeowner_oop on pre-Apr-24 rows
    const hoOop = ad.ho_out_of_pocket ?? ad.homeowner_oop ?? 0;
    return {
      first_name: firstName(deal.homeowner_name),
      homeowner_name: deal.homeowner_name ?? "",
      first_payment_to_bardin: money(ad.first_payment_to_bardin),
      ho_out_of_pocket: money(hoOop),
      preferred_build_date: fmtDate(deal.ho_preferred_build_date),
      signing_link: "https://portal.bardinroofing.com/sign?token=[token]",
      // sender_name reflects the deal owner (matches what the edge function uses on send).
      // Falls back to caller's name if owner row hasn't loaded yet.
      sender_name: ownerRm?.full_name ?? currentUserFullName ?? "the Bardin Roofing team",
      address: [deal.property_street, deal.property_city, deal.property_state].filter(Boolean).join(", "),
    };
  }, [deal, currentUserFullName, ownerRm]);

  const handleSend = useCallback(async (method: "email" | "sms" | "both") => {
    setSending(method);
    setErr(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetchEdge(
        `${SUPABASE_URL}/functions/v1/install-agreement-send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({ deal_id: deal.id, method }),
        },
      );
      const result = await readEdgeJson<{ ok?: boolean; detail?: string; error?: string }>(resp);
      if (!result?.ok) {
        throw new Error(result?.detail ?? result?.error ?? `HTTP ${resp.status}`);
      }
      onSent();
      onClose();
    } catch (e: any) {
      setErr(`Send failed: ${e?.message ?? e}`);
    } finally {
      setSending(null);
    }
  }, [supabase, deal.id, onSent, onClose]);

  const previewSubject = templates ? mergeTemplate(templates.emailSubject, previewVars) : "";
  const previewEmail   = templates ? mergeTemplate(templates.emailBody, previewVars) : "";
  const previewSms     = templates ? mergeTemplate(templates.smsBody, previewVars) : "";

  return (
    <div className="sa-modal-overlay" onClick={onClose}>
      <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sa-modal-head">
          <div>
            <h3>Send install agreement</h3>
            <div className="sa-sub">{deal.homeowner_name} · {deal.property_street}</div>
          </div>
          <button className="sa-x" onClick={onClose}>✕</button>
        </div>
        <div className="sa-modal-body">
          {templates && (
            <div className="sa-preview">
              <div className="sa-preview-head">
                <span className="sa-preview-label">
                  Message preview · {templates.isPersonalOverride ? "your personal template" : "system default"}
                </span>
                <div className="sa-tabs">
                  <button className={`sa-tab ${previewTab === "email" ? "active" : ""}`} onClick={() => setPreviewTab("email")}>📧 Email</button>
                  <button className={`sa-tab ${previewTab === "sms" ? "active" : ""}`} onClick={() => setPreviewTab("sms")}>💬 Text</button>
                </div>
              </div>
              <div className="sa-preview-body">
                {previewTab === "email" ? (
                  <>
                    <div className="sa-preview-meta">
                      <b>To:</b> {deal.homeowner_email ?? "—"}
                      {" · "}
                      <b>From:</b> {ownerRm?.full_name ?? "—"} {ownerRm?.email ? `<${ownerRm.email}>` : ""}
                      {splitRm?.email && splitRm.email !== ownerRm?.email && (
                        <>{" · "}<b>CC:</b> {splitRm.full_name ?? splitRm.email}</>
                      )}
                    </div>
                    <div className="sa-preview-subject"><b>Subject:</b> {previewSubject}</div>
                    <pre className="sa-preview-text">{previewEmail}</pre>
                  </>
                ) : (
                  <>
                    <div className="sa-preview-meta"><b>To:</b> {deal.homeowner_phone ?? "—"} · <b>From:</b> (470) 823-8816</div>
                    <pre className="sa-preview-text sa-preview-sms">{previewSms}</pre>
                  </>
                )}
              </div>
            </div>
          )}
          {err && <div className="sa-err">{err}</div>}
        </div>
        <div className="sa-modal-foot">
          <button className="sa-btn-ghost" onClick={() => setEditOpen(true)} disabled={!!sending}>✏️ Edit message</button>
          <div className="sa-foot-spacer" />
          <button className="sa-btn-secondary" onClick={() => handleSend("email")} disabled={!!sending}>
            {sending === "email" ? "Sending…" : "📧 Email"}
          </button>
          <button className="sa-btn-secondary" onClick={() => handleSend("sms")} disabled={!!sending}>
            {sending === "sms" ? "Sending…" : "💬 Text"}
          </button>
          <button className="sa-btn-primary" onClick={() => handleSend("both")} disabled={!!sending}>
            {sending === "both" ? "Sending…" : "📧💬 Send both"}
          </button>
        </div>
      </div>

      {editOpen && templates && (
        <AgreementTemplateEditModal
          supabase={supabase}
          currentUserId={currentUserId}
          initial={templates}
          previewVars={previewVars}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); loadTemplates(); }}
        />
      )}

      <style>{modalStyles}</style>
    </div>
  );
}

// --- agreement template edit modal (mirrors invoice version) -------------

function AgreementTemplateEditModal({
  supabase, currentUserId, initial, previewVars, onClose, onSaved,
}: {
  supabase: SupabaseClient;
  currentUserId: string;
  initial: TemplateSet;
  previewVars: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [subj, setSubj] = useState(initial.emailSubject);
  const [body, setBody] = useState(initial.emailBody);
  const [sms, setSms]   = useState(initial.smsBody);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const { error } = await supabase
        .from("agreement_template_overrides")
        .upsert({
          user_id: currentUserId,
          email_subject: subj, email_body: body, sms_body: sms,
          updated_at: new Date().toISOString(),
        }, { onConflict: "user_id" });
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(`Save failed: ${e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm("Clear your personal template and go back to the system default?")) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("agreement_template_overrides").delete().eq("user_id", currentUserId);
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(`Reset failed: ${e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sa-modal-overlay" style={{ zIndex: 200 }} onClick={onClose}>
      <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sa-modal-head">
          <h3>Edit your agreement templates</h3>
          <button className="sa-x" onClick={onClose}>✕</button>
        </div>
        <div className="sa-modal-body">
          <p className="sa-hint">
            Your personal templates. System defaults stay untouched. Merge fields:
            <code>{` {first_name} `}</code><code>{` {first_payment_to_bardin} `}</code>
            <code>{` {ho_out_of_pocket} `}</code><code>{` {preferred_build_date} `}</code>
            <code>{` {signing_link} `}</code><code>{` {sender_name} `}</code>
          </p>
          <div className="sa-section">
            <label>📧 Email subject</label>
            <input className="sa-input" value={subj} onChange={(e) => setSubj(e.target.value)} />
            <div className="sa-rendered"><span className="sa-rendered-label">Preview:</span> {mergeTemplate(subj, previewVars)}</div>
          </div>
          <div className="sa-section">
            <label>📧 Email body</label>
            <textarea className="sa-textarea" rows={12} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="sa-section">
            <label>💬 SMS body</label>
            <textarea className="sa-textarea" rows={5} value={sms} onChange={(e) => setSms(e.target.value)} />
            <div className="sa-rendered"><span className="sa-rendered-label">Preview:</span> {mergeTemplate(sms, previewVars)}</div>
            <div className="sa-meta">{sms.length} chars · {Math.ceil(sms.length / 160)} SMS segment(s)</div>
          </div>
          {err && <div className="sa-err">{err}</div>}
        </div>
        <div className="sa-modal-foot">
          {initial.isPersonalOverride && (
            <button className="sa-btn-ghost" onClick={handleReset} disabled={saving}>↺ Reset to system default</button>
          )}
          <div className="sa-foot-spacer" />
          <button className="sa-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="sa-btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save my template"}</button>
        </div>
      </div>
    </div>
  );
}

// --- HERO export — big card at top of DealDetail --------------------------

export function SendAgreementHero(props: Props) {
  const { supabase, deal, onUpdated } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);

  const loadDeliveries = useCallback(async () => {
    const { data } = await supabase
      .from("deal_agreement_deliveries")
      .select("id, sent_at, sent_by, method, recipient_email, recipient_phone, email_status, sms_status, first_opened_at, open_count, signed_at, signed_by_name, signing_link_url")
      .eq("deal_id", deal.id)
      .is("revoked_at", null)
      .order("sent_at", { ascending: false });
    setDeliveries((data ?? []) as Delivery[]);
  }, [supabase, deal.id]);

  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  useEffect(() => {
    const ch = supabase
      .channel(`dad-${deal.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "deal_agreement_deliveries", filter: `deal_id=eq.${deal.id}` },
        () => loadDeliveries(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [supabase, deal.id, loadDeliveries]);

  // Don't render at all if not in the relevant lifecycle window
  if (deal.agreement_status !== "approved" && !deal.install_agreement_signed_date) {
    return null;
  }

  // Already signed
  if (deal.install_agreement_signed_date) {
    return (
      <div className="sa-hero sa-hero--signed">
        <div className="sa-hero-icon">✅</div>
        <div className="sa-hero-body">
          <div className="sa-hero-title">Install agreement signed</div>
          <div className="sa-hero-sub">
            {deal.homeowner_name} signed {relativeTime(deal.install_agreement_signed_date)}
            {deliveries[0]?.signed_by_name && deliveries[0].signed_by_name !== deal.homeowner_name &&
              ` · as ${deliveries[0].signed_by_name}`}
          </div>
        </div>
        {deal.install_agreement_pdf_url && (
          <button className="sa-btn-secondary" onClick={async () => {
            const { data } = await supabase.storage.from("deal-documents").createSignedUrl(deal.install_agreement_pdf_url!, 3600);
            if (data?.signedUrl) window.open(data.signedUrl, "_blank");
          }}>📄 View signed PDF</button>
        )}
        <style>{heroStyles}</style>
      </div>
    );
  }

  // Approved, awaiting send / signature
  const latest = deliveries[0];
  const hasSent = !!latest;
  const hasOpened = !!latest?.first_opened_at;

  return (
    <>
      <div className="sa-hero">
        <div className="sa-hero-icon">📝</div>
        <div className="sa-hero-body">
          <div className="sa-hero-title">
            {hasSent ? "Awaiting homeowner signature" : "Ready to send to homeowner"}
          </div>
          <div className="sa-hero-sub">
            {!hasSent && "Chad approved the install agreement. Tap below to send the signing link."}
            {hasSent && !hasOpened && `Sent ${relativeTime(latest.sent_at)} via ${latest.method === "both" ? "email + text" : latest.method} · not yet opened.`}
            {hasSent && hasOpened && !latest.signed_at && `Sent ${relativeTime(latest.sent_at)} · opened ${relativeTime(latest.first_opened_at)} · ${latest.open_count} view${latest.open_count > 1 ? "s" : ""}.`}
          </div>
        </div>
        <button className="sa-btn-primary sa-btn-big" onClick={() => setModalOpen(true)}>
          📤 {hasSent ? "Resend agreement" : "Send install agreement"}
        </button>
      </div>

      {deliveries.length > 0 && (
        <div className="sa-history">
          <div className="sa-history-label">Delivery history</div>
          {deliveries.map((d) => <AgreementDeliveryRow key={d.id} d={d} />)}
        </div>
      )}

      {modalOpen && (
        <SendAgreementModal
          {...props}
          onClose={() => setModalOpen(false)}
          onSent={() => { loadDeliveries(); onUpdated?.(); }}
        />
      )}

      <style>{heroStyles}</style>
    </>
  );
}

// --- BUTTON export — compact, for deal cards ------------------------------

export function SendAgreementButton(props: Props) {
  const { deal } = props;
  const [modalOpen, setModalOpen] = useState(false);

  if (!canSend(deal)) return null;

  return (
    <>
      <button className="sa-btn-primary sa-btn-card" onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}>
        📤 Send install agreement
      </button>
      {modalOpen && (
        <SendAgreementModal
          {...props}
          onClose={() => setModalOpen(false)}
          onSent={() => { props.onUpdated?.(); }}
        />
      )}
      <style>{buttonStyles}</style>
    </>
  );
}

// --- Delivery row ---------------------------------------------------------

function AgreementDeliveryRow({ d }: { d: Delivery }) {
  const sentLabel = d.method === "both" ? "Email + SMS" : d.method === "email" ? "Email" : "SMS";
  const signed = !!d.signed_at;
  const opened = !!d.first_opened_at;
  return (
    <div className="sa-hist-item">
      <span className={`sa-hist-dot ${signed ? "signed" : opened ? "opened" : "sent"}`}>
        {signed ? "✓" : opened ? "👁" : "→"}
      </span>
      <div className="sa-hist-body">
        <div className="sa-hist-line">
          <b>{sentLabel}</b> · {relativeTime(d.sent_at)}
          {opened && !signed && <span className="sa-hist-tag">opened {relativeTime(d.first_opened_at)}</span>}
          {signed && <span className="sa-hist-tag sa-hist-tag--signed">SIGNED {relativeTime(d.signed_at)}</span>}
        </div>
        <div className="sa-hist-sub">
          {d.recipient_email && <span>{d.recipient_email}</span>}
          {d.recipient_email && d.recipient_phone && <span> · </span>}
          {d.recipient_phone && <span>{d.recipient_phone}</span>}
          {d.open_count > 1 && <span> · {d.open_count} views</span>}
        </div>
      </div>
    </div>
  );
}

// --- styles ---------------------------------------------------------------

const heroStyles = `
.sa-hero {
  display: flex; align-items: center; gap: 14px;
  background: linear-gradient(135deg, #fff1ea 0%, #ffe1d1 100%);
  border: 2px solid #f46530; border-radius: 14px;
  padding: 18px 20px; margin: 12px 0 16px;
}
.sa-hero--signed {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #10b981;
}
.sa-hero-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: white; display: flex; align-items: center; justify-content: center;
  font-size: 24px; flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.06);
}
.sa-hero-body { flex: 1; min-width: 0; }
.sa-hero-title { font-size: 16px; font-weight: 800; color: #2a2e30; }
.sa-hero-sub { font-size: 13px; color: #5b6266; margin-top: 4px; line-height: 1.5; }
.sa-btn-big {
  padding: 14px 24px !important;
  font-size: 15px !important;
  white-space: nowrap;
  flex-shrink: 0;
}
.sa-history {
  background: white; border: 1px solid #e3e6e8; border-radius: 12px;
  padding: 14px 16px; margin: 0 0 16px;
}
.sa-history-label {
  font-size: 11px; text-transform: uppercase; font-weight: 700;
  color: #5b6266; letter-spacing: 0.4px; margin-bottom: 10px;
}
.sa-hist-item { display: flex; align-items: flex-start; gap: 10px; padding: 6px 0; }
.sa-hist-dot {
  width: 24px; height: 24px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; flex-shrink: 0;
}
.sa-hist-dot.sent { background: #fff1ea; color: #f46530; }
.sa-hist-dot.opened { background: #eff6ff; color: #3b82f6; }
.sa-hist-dot.signed { background: #ecfdf5; color: #10b981; }
.sa-hist-body { flex: 1; }
.sa-hist-line { font-size: 12.5px; color: #2a2e30; }
.sa-hist-line b { font-weight: 700; }
.sa-hist-tag {
  display: inline-block; margin-left: 8px;
  font-size: 10px; background: #eff6ff; color: #3b82f6;
  padding: 2px 8px; border-radius: 4px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.sa-hist-tag--signed { background: #ecfdf5; color: #10b981; }
.sa-hist-sub { font-size: 11px; color: #5b6266; margin-top: 1px; }
.sa-btn-primary {
  background: #f46530; color: white; border: none;
  padding: 10px 18px; border-radius: 10px;
  font-weight: 700; font-size: 14px; cursor: pointer;
  box-shadow: 0 1px 3px rgba(244,101,48,0.3);
}
.sa-btn-primary:hover { background: #e25624; }
.sa-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
.sa-btn-secondary {
  background: white; color: #2a2e30;
  border: 1px solid #e3e6e8; padding: 10px 18px;
  border-radius: 10px; font-weight: 600; font-size: 14px; cursor: pointer;
}
.sa-btn-secondary:hover { border-color: #f46530; color: #f46530; }
`;

const buttonStyles = `
.sa-btn-card {
  display: inline-flex; align-items: center; gap: 6px;
  background: #f46530; color: white; border: none;
  padding: 10px 16px; border-radius: 10px;
  font-weight: 700; font-size: 13px; cursor: pointer;
  box-shadow: 0 1px 3px rgba(244,101,48,0.3);
  width: 100%;
  justify-content: center;
}
.sa-btn-card:hover { background: #e25624; }
`;

const modalStyles = `
.sa-modal-overlay {
  position: fixed; inset: 0; background: rgba(42,46,48,0.55);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 40px 20px; z-index: 100; overflow-y: auto;
}
.sa-modal {
  background: white; width: 100%; max-width: 720px;
  border-radius: 14px; box-shadow: 0 12px 32px rgba(0,0,0,0.2);
  overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
}
.sa-modal-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 16px 22px; border-bottom: 1px solid #e3e6e8;
}
.sa-modal-head h3 { margin: 0; font-size: 17px; font-weight: 700; color: #2a2e30; }
.sa-sub { font-size: 12px; color: #5b6266; margin-top: 2px; }
.sa-x { background: none; border: none; font-size: 20px; color: #9aa0a4; cursor: pointer; }
.sa-modal-body { padding: 22px; }
.sa-preview {
  background: white; border: 1px solid #e3e6e8; border-radius: 10px; overflow: hidden;
}
.sa-preview-head {
  display: flex; align-items: center;
  background: #f3f5f6; border-bottom: 1px solid #e3e6e8;
  padding: 10px 12px;
}
.sa-preview-label {
  font-size: 11px; text-transform: uppercase; font-weight: 700;
  color: #5b6266; letter-spacing: 0.4px;
}
.sa-tabs { display: flex; gap: 4px; margin-left: auto; }
.sa-tab {
  font-size: 11px; padding: 4px 10px;
  background: white; color: #5b6266;
  border: 1px solid #e3e6e8; border-radius: 6px;
  font-weight: 600; cursor: pointer;
}
.sa-tab.active { background: #f46530; color: white; border-color: #f46530; font-weight: 700; }
.sa-preview-body { padding: 14px 16px; }
.sa-preview-meta { font-size: 11px; color: #5b6266; margin-bottom: 6px; }
.sa-preview-subject { font-size: 12px; color: #5b6266; margin-bottom: 10px; }
.sa-preview-text {
  font-size: 13px; line-height: 1.55; color: #2a2e30;
  white-space: pre-wrap; word-wrap: break-word;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  margin: 0;
}
.sa-preview-sms {
  background: #f3f5f6; border-radius: 12px;
  padding: 10px 14px; max-width: 380px;
}
.sa-err {
  margin-top: 12px; padding: 10px 12px;
  background: #fff1ea; border: 1px solid #ff9e7a;
  border-radius: 8px; font-size: 13px; color: #2a2e30;
}
.sa-hint {
  background: #fff1ea; border: 1px solid #ff9e7a; border-radius: 8px;
  padding: 10px 14px; font-size: 12px; color: #2a2e30; line-height: 1.5;
  margin: 0 0 18px;
}
.sa-hint code { background: white; padding: 1px 4px; border-radius: 3px; font-size: 11px; color: #f46530; font-weight: 600; margin: 0 1px; }
.sa-section { margin-bottom: 18px; }
.sa-section label {
  display: block; font-size: 11px; font-weight: 700;
  color: #5b6266; text-transform: uppercase; letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.sa-input, .sa-textarea {
  width: 100%; padding: 10px 12px;
  border: 1px solid #e3e6e8; border-radius: 8px;
  font-size: 14px; font-family: inherit; color: #2a2e30;
  line-height: 1.5; resize: vertical;
}
.sa-rendered {
  margin-top: 6px; padding: 8px 10px; background: #f3f5f6;
  border-radius: 6px; font-size: 12px; color: #2a2e30; line-height: 1.5;
  white-space: pre-wrap;
}
.sa-rendered-label { color: #5b6266; font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.4px; }
.sa-meta { font-size: 11px; color: #9aa0a4; margin-top: 4px; }
.sa-modal-foot {
  display: flex; align-items: center; gap: 8px;
  padding: 14px 22px; background: #f3f5f6;
  border-top: 1px solid #e3e6e8;
}
.sa-foot-spacer { flex: 1; }
.sa-btn-ghost {
  background: white; color: #5b6266;
  border: 1px solid #e3e6e8; padding: 9px 14px;
  border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer;
}
.sa-btn-ghost:hover { border-color: #f46530; color: #f46530; }
`;
